import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../utils/api';
import ChatSidebarLayout from './ChatSidebarLayout';
import { getAutoResponse } from './utils/autoResponses';
import { shouldAutoExpand, ROUTE_MAPPING, createTaskFlowHandlers, createModalHandlers, getContextualPlaceholder } from './utils/chatSidebarUtils';
import { 
  createUserMessage, 
  createAgentMessage, 
  createTourMessage, 
  createErrorMessage, 
  createSystemMessage, 
  createChoiceCardSuccessMessage, 
  createMockResponseMessage,
  createMessageFromDB
} from './utils/messageCreators';
import { mockResponses } from './utils/chatSidebarMockResponses';
import { useSparkleTrail } from '../../hooks2/useSparkleTrail';
import { useDOMCapture } from '../../hooks2/useDOMCapture';
import { tourManager } from '../../utils/TourManager';
import useAsyncPolling from '../../hooks2/useAsyncPolling';
import useAgentActions from '../../hooks2/useAgentActions';
import useTutorials from '../../hooks2/useTutorials';
import useTourManagement from '../../hooks2/useTourManagement';
import HistoricalDataAnalysisIndicator from './HistoricalDataAnalysisIndicator';
import { useChatContext } from '../../hooks/useChatContext';
import { useChatState } from '../../contexts/ChatStateContext';

const ChatSidebar = ({ 
  darkMode = false,
  currentContext = null,
  onSendMessage = () => {},
  onNavigateToAnalysis = () => {},
  isCollapsed = false,
  onToggleCollapse = () => {},
  analysisData = null, // For page mentions
  progressiveAnalysisState = null, // For tracking analysis progress
  isAnalysisRunning = false, // For tracking analysis state
  onLaunchLLMPortal = () => {}, // For launching AI Playground
  refreshTrigger = 0, // Increment this to trigger conversation refresh
  className = "",
  // Portal mode props
  portalMode = false, // When true, optimizes for portal context
  portalContext = null, // Portal-specific analysis results
  agentWelcomeMessage = null, // Custom welcome message from agent
  showNavigation = true, // Hide nav features in portal
  showTabs = true, // Hide tabs in portal for focused chat
  recentAIAnalyses = { promptBurst: null, aiCrawler: null }, // Recent AI analysis results for agent context
  historicalDataPolling = null, // Historical data polling state for enhanced onboarding
  currentDomain = null, // Current analyzed domain for smart link handling
  // Analysis settings props for integrated settings tab
  analysisSettings = {},
  // Property-scoped chat props (now handled by context)
  propertyUrl = null, // Current GSC property URL for conversation scoping
  onPropertyChange = () => {}, // Callback when property changes (deprecated - use context)
  isChangingProperty = false, // Flag to prevent property change handling during transitions (deprecated - use context)
  // Tutorial mode props
  tutorialContentCapture = null, // DOM-captured tutorial content
  tutorialMetadata = null // Tutorial metadata (title, implementation_context, etc.)
}) => {
  // 🎯 CHAT STATE CONTEXT - Single source of truth for chat state
  const { backendChatState, pendingActions, clearActions } = useChatState();

  // Get state from context (only runtime UI state, not backend-driven state)
  const {
    messages,
    isTyping,
    currentPropertyUrl,
    isChangingProperty: contextIsChangingProperty,
    propertyChangeState,
    tourInProgress,
    setMessages,
    setIsTyping,
    setTourInProgress,
    handlePropertyChange,
    handleStartNewConversation,
    handleTourStart,
    handleTourComplete,
    handleTourStop,
    loadConversationHistory
  } = useChatContext();

  // 🎯 DERIVE MESSAGES FROM BACKEND STATE
  // This function converts backend chat_state into displayable messages
  const deriveMessagesFromBackend = (chatState, hasTourPending = false) => {
    console.log('🎯 [ChatSidebar] Deriving messages from backend state:', { chatState, hasTourPending });
    
    // If backend provides messages, use them (converted from DB format)
    if (chatState?.messages && chatState.messages.length > 0) {
      console.log('📚 [ChatSidebar] Using backend conversation history:', chatState.messages.length, 'messages');
      return chatState.messages.map(msg => createMessageFromDB(msg));
    }
    
    // No messages - generate welcome message based on mode and flags
    const mode = chatState?.mode || 'normal';
    const isOnboarding = chatState?.is_onboarding || false;
    const propertyMatchFound = chatState?.property_match_found || false;
    const matchedProperty = chatState?.matched_property;
    
    console.log('🎯 [ChatSidebar] Generating welcome message - mode:', mode, 'isOnboarding:', isOnboarding, 'propertyMatchFound:', propertyMatchFound, 'hasTourPending:', hasTourPending);
    
    // Helper to extract domain from property URL (handles sc-domain: prefix)
    const extractDomain = (propertyUrl) => {
      if (!propertyUrl) return 'your site';
      
      // Handle sc-domain: properties
      if (propertyUrl.startsWith('sc-domain:')) {
        return propertyUrl.replace('sc-domain:', '');
      }
      
      // Handle URL-prefix properties
      try {
        return new URL(propertyUrl).hostname;
      } catch {
        return propertyUrl; // Fallback to raw value
      }
    };
    
    // ONBOARDING FLOW - No property match (Matrix flow - show property selector)
    if (isOnboarding && !propertyMatchFound) {
      console.log('🎯 [ChatSidebar] Onboarding without property match - showing property selector');
      return [createAgentMessage(
        `🔴 **Choose Your Reality**\n\nI couldn't find a matching Search Console property for the domain you analyzed.\n\n**Please select which property you'd like to analyze:**\n\nYour GSC properties are available in the property selector.`,
        { context: { type: 'property_selector_welcome' } },
        0
      )];
    }
    
    // ONBOARDING FLOW - Property matched + auto-tour trigger
    if (isOnboarding && propertyMatchFound && hasTourPending) {
      const domainName = extractDomain(matchedProperty);
      console.log('🎯 [ChatSidebar] Onboarding with property match - auto-starting tour for:', domainName);
      return [createAgentMessage(
        `Perfect! I can see **${domainName}** in your Search Console. I'm running a comprehensive analysis for the past 30 days to show you exactly what's happening with your SEO performance...\n\n⏳ This usually takes 30-60 seconds. I'll walk you through the results when it's ready!`,
        { 
          context: { type: 'onboarding_analysis_running', propertyUrl: matchedProperty }
        },
        0
      )];
    }
    
    // ONBOARDING FLOW - Property matched but no auto-tour (manual trigger)
    if (isOnboarding && propertyMatchFound) {
      const domainName = extractDomain(matchedProperty);
      console.log('🎯 [ChatSidebar] Onboarding with property match - showing manual trigger buttons');
      return [createAgentMessage(
        `👋 **Welcome to your SEO Assistant!**\n\nI'm here to help you understand **${domainName}**'s SEO performance and guide you to the right insights.\n\nLet's start by running a comprehensive analysis to understand your site's current performance and identify key opportunities.\n\n**Ready to begin?**`,
        { 
          context: { type: 'onboarding_welcome', propertyUrl: matchedProperty },
          showInlineButtons: true,
          inlineButtons: [
            { text: 'Run Initial Assessment', action: 'run_initial_assessment', variant: 'primary' },
            { text: 'Explore on My Own', action: 'explore_alone', variant: 'secondary' }
          ]
        },
        0
      )];
    }
    
    // No property mode
    if (mode === 'no_property') {
      return [createAgentMessage(
        `👋 **Welcome to your SEO Assistant!**\n\nTo get started, please connect your Google Search Console and select a property to analyze.\n\nOnce connected, I'll help you understand your SEO performance and identify opportunities for improvement.`,
        { context: { type: 'no_property_welcome' } },
        0
      )];
    }
    
    // Normal mode - default welcome
    return [createAgentMessage(
      "👋 **Welcome to your SEO Assistant!**\n\nI'm here to help you understand your SEO performance and guide you to the right insights. I can:\n\n• **Analyze your traffic** and keyword performance\n• **Explain technical issues** and opportunities  \n• **Navigate you to specific reports** with smart filters\n• **Answer SEO questions** based on your data\n\n**Try asking me:**\n• \"Show me my brand keywords\"\n• \"Find keyword opportunities\" \n• \"Which pages are losing traffic?\"\n• \"Help me understand this page's performance\"\n• \"What should I work on first?\"\n\nWhat would you like to explore first?",
      { context: { type: 'welcome' } },
      0
    )];
  };

  // 🎯 INITIALIZE MESSAGES FROM BACKEND STATE
  // This replaces the old ChatContext.initializeMessages() complex logic
  useEffect(() => {
    // Only initialize if we have backend state and NOT in portal mode
    if (!portalMode && backendChatState) {
      console.log('🎯 [ChatSidebar] Backend state changed - reinitializing messages');
      
      // Check if tour should auto-start based on backend state
      // (is_onboarding + property_match_found = auto-start tour)
      const shouldAutoStartTour = backendChatState.is_onboarding && backendChatState.property_match_found;
      
      console.log('🎯 [ChatSidebar] Auto-start tour?', {
        isOnboarding: backendChatState.is_onboarding,
        propertyMatchFound: backendChatState.property_match_found,
        shouldAutoStartTour
      });
      
      const derivedMessages = deriveMessagesFromBackend(backendChatState, shouldAutoStartTour);
      setMessages(derivedMessages);
    }
  }, [backendChatState, portalMode]); // Reinit when backend state changes

  // Helper function for date calculations
  const getDateDaysAgo = (days) => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date.toISOString().split('T')[0];
  };

  // Local state for UI-only concerns
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('chat'); // 'chat', 'context', 'settings'
  const [currentAbortController, setCurrentAbortController] = useState(null);
  
  // Choice Cards Modal state
  const [isChoiceCardsModalOpen, setIsChoiceCardsModalOpen] = useState(false);
  const [choiceCardsData, setChoiceCardsData] = useState(null);
  const [isExpandedMessageModalOpen, setIsExpandedMessageModalOpen] = useState(false);
  const [expandedMessage, setExpandedMessage] = useState(null);
  // Note: autoExpandingMessage removed for performance - manual expand only
  
  // Task Creation state
  const [isTaskFlowActive, setIsTaskFlowActive] = useState(false);
  const [activeTaskCard, setActiveTaskCard] = useState(null);
  
  // Settings view state
  const [showSidebarForm, setShowSidebarForm] = useState(false);
  
  // Note: recentAIAnalyses now comes from props (managed by App.jsx)
  
  // Tutorials hook
  const {
    isTutorialModeActive,
    activeTutorialData,
    tutorialTransitionState,
    isAvatarJourneyActive,
    loadTutorialFromStorage,
    reopenLastTutorial,
    handleTutorialStart,
    handleTutorialClose,
    handleAvatarJourneyComplete,
    handleDirectTutorialCreation,
    handleTutorialCreation
  } = useTutorials({ messages, setMessages });

  // Sparkle trail hook for delightful agent action feedback
  const {
    isTrailActive,
    activeTrail,
    sourceElement,
    targetElement,
    detectAgentAction,
    completeTrail,
    highlightDestination
  } = useSparkleTrail();
  
  // DOM capture hook for agent context
  const { capturePageContext } = useDOMCapture();

  // Handle task completion callback for async polling
  const handleTaskComplete = (resultData) => {
    // The result data contains the actual response data
    const responseData = resultData;
    
    // Handle successful agent response
    const agentMessage = createAgentMessage(responseData.response, {
      toolsUsed: responseData.tools_used,
      rationale: responseData.rationale,
      action: responseData.action,
      actionData: responseData.data
    }, messages.length);

    // Note: Auto-expand removed for better performance - users can manually expand long messages

    setMessages(prev => [...prev, agentMessage]);
    setIsTyping(false); // Stop typing indicator

    // Trigger sparkle trail for agent actions
    if (responseData.action) {
      detectAgentAction(responseData);
    }

    // Handle all the action types (navigation, choice cards, etc.)
    handleAgentActions(responseData);
    
    // Call the actual send handler for consistency
    onSendMessage(responseData.message || 'Agent response', currentContext);
  };

  // Async polling hook
  const {
    isPolling,
    currentProgress,
    currentProgressMessage,
    currentTaskId,
    startPolling,
    stopPolling
  } = useAsyncPolling({ messages, setMessages, onTaskComplete: handleTaskComplete });

  // Agent actions hook
  const { handleAgentActions } = useAgentActions({
    showNavigation,
    portalMode,
    currentContext,
    onNavigateToAnalysis,
    onLaunchLLMPortal,
    setChoiceCardsData,
    setIsChoiceCardsModalOpen,
    handleDirectTutorialCreation,
    handleTutorialStart,
    tourManager
  });
  
  // Task flow handlers
  const {
    handleTaskGenerated,
    handleStepComplete,
    handleTaskComplete: handleTaskFlowComplete,
    handleTaskClose
  } = createTaskFlowHandlers(setIsTaskFlowActive, setActiveTaskCard);
  
  // Modal handlers
  const {
    handleExpandMessage,
    handleCloseExpandedMessage,
    handleChoiceCardsClose
  } = createModalHandlers(
    setExpandedMessage,
    setIsExpandedMessageModalOpen,
    setIsChoiceCardsModalOpen,
    setChoiceCardsData,
    portalMode
  );
  
  // Note: AI analysis completion now handled via props from App.jsx (proper React pattern)

  // Load conversation history is now handled by context

  // Clear conversation history
  const handleClearConversation = async () => {
    try {
      const response = await apiRequest('/api/agent/clear-conversation/', {
        method: 'DELETE',
        body: JSON.stringify({
          property_url: propertyUrl
        })
      });
      
      if (response.ok) {
        console.log('🗑️ [ChatSidebar] Conversation cleared successfully');
        // Reset to default welcome message
        setMessages([createAgentMessage(
          "👋 **Welcome to your SEO Assistant!**\n\nI'm here to help you understand your SEO performance and guide you to the right insights. I can:\n\n• **Analyze your traffic** and keyword performance\n• **Explain technical issues** and opportunities  \n• **Navigate you to specific reports** with smart filters\n• **Answer SEO questions** based on your data\n\n**Try asking me:**\n• \"Show me my brand keywords\"\n• \"Find keyword opportunities\" \n• \"Which pages are losing traffic?\"\n• \"Help me understand this page's performance\"\n• \"What should I work on first?\"\n\nWhat would you like to explore first?",
          {
            context: null
          },
          0
        )]);
      }
    } catch (error) {
      console.error('🗑️ [ChatSidebar] Failed to clear conversation:', error);
    }
  };

  // Listen for property changes - backend state will update via ChatStateContext
  useEffect(() => {
    if (propertyUrl && propertyUrl !== currentPropertyUrl) {
      console.log('🔄 [ChatSidebar] Property change detected - backend will provide new state');
      handlePropertyChange(propertyUrl);
      // Note: Backend state update will trigger message reinitialization automatically
    }
  }, [propertyUrl, currentPropertyUrl, handlePropertyChange]);

  // Listen for refresh trigger from parent (portal close) - refresh conversation
  useEffect(() => {
    if (refreshTrigger > 0) {
      console.log('🔄 [ChatSidebar] Refreshing conversation after portal activity...');
      // Reload the current conversation to pick up any new messages
      // This preserves existing conversation history while refreshing state
      loadConversationHistory(currentPropertyUrl);
    }
  }, [refreshTrigger, loadConversationHistory, currentPropertyUrl]);

  // Portal mode initialization - separate from backend state (portal is standalone context)
  useEffect(() => {
    if (portalMode && agentWelcomeMessage) {
      console.log('🎯 [ChatSidebar] Portal mode - using custom welcome message');
      setMessages([createAgentMessage(agentWelcomeMessage, {
        context: { type: 'portal_carryover' },
        isWelcome: true,
        isPortalMode: true
      }, 0)]);
    }
  }, [portalMode, agentWelcomeMessage]);

  // Tour management handlers
  useEffect(() => {
    // Register chat messaging handler for tours
    const handleChatMessaging = (actionData) => {
      const { action, message, stepIndex, totalSteps, tourType } = actionData;
      
      if (action === 'start_typing') {
        setIsTyping(true);
        handleTourStart();
      } else if (action === 'send_tour_message') {
        // Stop typing and add message
        setIsTyping(false);
        
        const tourMessage = createTourMessage(message, {
          stepIndex,
          totalSteps,
          tourType
        }, messages.length);

        setMessages(prev => [...prev, tourMessage]);
      } else if (action === 'tour_stopped') {
        handleTourStop();
        setIsTyping(false);
      }
    };

    // Register navigation handler for tours
    const handleNavigation = (actionData) => {
      const { action, data, rationale } = actionData;
      
      if (action === 'navigate_to_analysis') {
        onNavigateToAnalysis('navigate_to_analysis', data);
      } else if (action === 'navigate_to_page') {
        onNavigateToAnalysis('navigate_to_page', data);
      } else if (action === 'navigate_to_overview') {
        onNavigateToAnalysis('navigate_to_overview', {});
      } else if (action === 'navigate_to_prompt_burst') {
        // Navigate to Prompt Burst analysis tool
        onNavigateToAnalysis('navigate_to_prompt_burst', {});
      } else if (action === 'auto_run_prompt_burst') {
        // Auto-run Prompt Burst with agent data
        onNavigateToAnalysis('auto_run_prompt_burst', data);
      } else if (action === 'auto_run_ai_crawler') {
        // Auto-run AI Crawler with agent data
        onNavigateToAnalysis('auto_run_ai_crawler', data);
      } else if (action === 'navigate_to_ai_crawler') {
        // Navigate to AI Crawler analysis tool
        onNavigateToAnalysis('navigate_to_ai_crawler', {});
      } else if (action === 'llm_handoff') {
        const { silentPrompt, context } = data || {};
        
        // 🎯 ENHANCED ONBOARDING: Handle tour completion with historical data awareness
        if (silentPrompt && context === 'tour_completion') {
          const isOnboardingAssessment = silentPrompt.includes('conduct initial assessment after new user onboard flow');
          
          if (isOnboardingAssessment) {
            console.log('🎯 [Tour] Onboarding assessment requested - checking historical data status');
            
            // Check if historical data collection is in progress
            if (historicalDataPolling?.isPolling) {
              console.log('🎯 [Tour] Historical data collection in progress - showing interim message...', historicalDataPolling.taskId);
              
              // Show custom historical data analysis component
              const interimMessage = createAgentMessage(
                "Historical data analysis", // Fallback content in case custom component doesn't render
                {
                  context: { type: 'onboarding_historical_analysis' }
                }
              );
              setMessages(prev => [...prev, interimMessage]);
              // Note: We don't set isTyping here since the custom component has its own animations
              
              // Store the onboarding message to send later
              window.pendingOnboardingMessage = {
                silentPrompt,
                handleSendMessage,
                timestamp: Date.now()
              };
              
              console.log('🎯 [Tour] Waiting for historical data collection to complete...');
              
            } else {
              console.log('🎯 [Tour] No historical data collection in progress - proceeding with enhanced assessment');
              
              // No historical data collection happening - proceed immediately
              setTimeout(() => {
                handleSendMessage(silentPrompt, { hiddenUserMessage: true });
              }, 1000);
            }
          } else {
            // Not an onboarding assessment - proceed normally
            setTimeout(() => {
              handleSendMessage(silentPrompt, { hiddenUserMessage: true });
            }, 1000);
          }
        } else if (silentPrompt) {
          // Regular LLM handoff - proceed normally
          setTimeout(() => {
            handleSendMessage(silentPrompt, { hiddenUserMessage: true });
          }, 1000);
        }
      }
    };

    // Register handlers with TourManager
    tourManager.registerActionHandler('chat_messaging', handleChatMessaging);
    tourManager.registerActionHandler('navigation', handleNavigation);

    return () => {
      tourManager.unregisterActionHandler('chat_messaging');
      tourManager.unregisterActionHandler('navigation');
    };
  }, [messages.length, onNavigateToAnalysis]); // Run once on component mount
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // 🎯 ONBOARDING FOLLOW-UP: Auto-trigger scripted analysis tour when FULL analysis finishes
  useEffect(() => {
    // Check if we have an onboarding analysis running and it just completed
    const hasOnboardingMessage = messages.some(msg => 
      msg.context?.type === 'onboarding_analysis_running'
    );
    
    // Full analysis is complete when:
    // 1. Basic analysis is done (!isAnalysisRunning && analysisData)
    // 2. Progressive analysis is fully complete (progressiveAnalysisState?.fullAnalysisComplete)
    const isBasicAnalysisComplete = !isAnalysisRunning && analysisData;
    const isProgressiveAnalysisComplete = progressiveAnalysisState?.fullAnalysisComplete === true;
    const isFullAnalysisComplete = isBasicAnalysisComplete && isProgressiveAnalysisComplete;
    
    
    if (hasOnboardingMessage && isFullAnalysisComplete) {
      // Check if we already triggered the tour or added completion message
      const hasTourTriggered = messages.some(msg => 
        msg.context?.type === 'onboarding_analysis_complete' || msg.isTourMessage
      );
      
      // Also check if tour is already active to prevent double-start
      const isTourAlreadyActive = tourManager.isTourActive();
      
      if (!hasTourTriggered && !isTourAlreadyActive) {
        
        setTimeout(() => {
          // Double-check tour isn't active before starting
          if (!tourManager.isTourActive()) {
            // Start the onboarding product tour immediately
            tourManager.startTour('onboarding', { 
              onboardingMode: true,
              triggerSource: 'full_analysis_completion'
            });
          }
        }, 2000); // Small delay to let the user see the analysis loaded
      }
    }
  }, [isAnalysisRunning, analysisData, progressiveAnalysisState?.fullAnalysisComplete]); // Removed 'messages' from dependencies to prevent re-runs on every message

  // 🎯 ENHANCED ONBOARDING: Listen for historical data completion to trigger pending onboarding message
  useEffect(() => {
    // Check if we have a pending onboarding message and historical data just completed
    if (window.pendingOnboardingMessage && historicalDataPolling?.isComplete) {
      const { silentPrompt, handleSendMessage: sendMessage, timestamp } = window.pendingOnboardingMessage;
      
      // Only proceed if the message is recent (within 5 minutes) to avoid stale triggers
      const messageAge = Date.now() - timestamp;
      if (messageAge < 5 * 60 * 1000) { // 5 minutes
        
        // Remove interim message (no typing indicator to stop since using custom component)
        setMessages(prev => prev.filter(msg => msg.context?.type !== 'onboarding_historical_analysis'));
        
        // Clear the pending message and send it
        window.pendingOnboardingMessage = null;
        setTimeout(() => {
          sendMessage(silentPrompt, { hiddenUserMessage: true });
        }, 100);
      } else {
        window.pendingOnboardingMessage = null;
      }
    }
    
    // Also handle error cases
    if (window.pendingOnboardingMessage && historicalDataPolling?.error) {
      const { silentPrompt, handleSendMessage: sendMessage } = window.pendingOnboardingMessage;
      
      // Remove interim message (no typing indicator to stop since using custom component)
      setMessages(prev => prev.filter(msg => msg.context?.type !== 'onboarding_historical_analysis'));
      
      // Clear the pending message and send it anyway
      window.pendingOnboardingMessage = null;
      setTimeout(() => {
        sendMessage(silentPrompt, { hiddenUserMessage: true });
      }, 100);
    }
  }, [historicalDataPolling?.isComplete, historicalDataPolling?.error]);

  // Handle auto-responses and special shortcuts
  const handleCannedResponse = (messageText) => {
    const message = messageText.toLowerCase().trim();
    
    // Try auto-responses first (thanks, greetings, positive acknowledgments)
    const autoResponse = getAutoResponse(messageText);
    if (autoResponse) {
      return autoResponse;
    }

    // Handle A, B, C shortcuts when Choice Cards are available
    if (['a', 'b', 'c', 'd', 'e'].includes(message) && choiceCardsData) {
      const cardIndex = message.charCodeAt(0) - 97; // a=0, b=1, c=2, etc.
      if (cardIndex < choiceCardsData.cards.length) {
        return `SELECT_CHOICE_CARD:${cardIndex}`;
      }
    }
    
    return null;
  };

  const handleSendMessage = async (messageText, options = {}) => {
    if (!messageText.trim()) return;

    // Check for canned responses first
    const cannedResponse = handleCannedResponse(messageText);
    if (cannedResponse && cannedResponse.startsWith('SELECT_CHOICE_CARD:')) {
      // Handle A, B, C shortcuts
      const cardIndex = parseInt(cannedResponse.split(':')[1]);
      const selectedCard = choiceCardsData.cards[cardIndex];
      
      // Add user message
      const userMessage = createUserMessage(messageText, {
        hiddenUserMessage: options.hiddenUserMessage
      }, messages.length);
      
      // Add agent response
      const agentMessage = createChoiceCardSuccessMessage(selectedCard, messages.length);
      
      setMessages(prev => [...prev, userMessage, agentMessage]);
      
      // Close modal and navigate
      setIsChoiceCardsModalOpen(false);
      handleChoiceCardSelect(selectedCard);
      
      return; // Skip API call
    } else if (cannedResponse) {
      // Handle other canned responses normally
      // Add user message
      const userMessage = createUserMessage(messageText, {
        hiddenUserMessage: options.hiddenUserMessage
      }, messages.length);
      
      // Add instant agent response
      const agentMessage = createAgentMessage(cannedResponse, {
        isCannedResponse: true
      }, messages.length);
      
      setMessages(prev => [...prev, userMessage, agentMessage]);
      return; // Skip API call
    }

    const userMessage = createUserMessage(messageText, {
      context: currentContext,
      hiddenUserMessage: options.hiddenUserMessage
    }, messages.length);

    setMessages(prev => [...prev, userMessage]);
    const currentMessage = messageText.trim();
    setMessage('');
    setIsTyping(true);

    // Check for mock responses (development/testing mode)
    const lowerMessage = currentMessage.toLowerCase().trim();
    
    const mockResponse = Object.keys(mockResponses).find(key => {
      const keyLower = key.toLowerCase();
      return lowerMessage.includes(keyLower) || lowerMessage === keyLower;
    });

    if (mockResponse) {
      const response = mockResponses[mockResponse];
      
      setTimeout(() => {
        const agentMessage = createMockResponseMessage(response, messages.length);

        setMessages(prev => [...prev, agentMessage]);
        setIsTyping(false);
        
        // Handle special actions
        if (response.action === 'present_choice_cards') {
          setTimeout(() => {
            setChoiceCardsData(response.actionData);
            setIsChoiceCardsModalOpen(true);
          }, 500);
        } else if (response.action === 'present_tutorial_cards') {
          setTimeout(() => {
            setChoiceCardsData(response.actionData);
            setIsChoiceCardsModalOpen(true);
          }, 500);
        }
      }, 1200);
      
      onSendMessage(currentMessage, currentContext);
      return;
    }

    // Capture current page context (table data and filters)
    const { tableData, filtersData } = capturePageContext();

    try {
      // Create abort controller for this request
      const abortController = new AbortController();
      setCurrentAbortController(abortController);

      // Determine effective context - portal mode overrides currentContext
      const effectiveContext = portalMode && portalContext ? {
        type: 'llm_portal_chat',
        route: '/llm-portal',
        data: {
          analysisResults: portalContext
        },
        summary: `LLM Mode portal chat - discussing AI citation analysis results`
      } : currentContext;

      // Call the async agent chat endpoint
      const agentResponse = await apiRequest('/api/agent/chat-async/', {
        method: 'POST',
        signal: abortController.signal,
        body: JSON.stringify({ 
          message: currentMessage,
          url: effectiveContext?.page || effectiveContext?.url || portalContext?.currentUrl || null,
          auditContext: effectiveContext?.data?.auditContext || null,
          contextInfo: {
            type: effectiveContext?.type || 'general',
            route: effectiveContext?.route || null,
            subType: effectiveContext?.subType || null,
            summary: effectiveContext?.summary || null,
            data: effectiveContext?.data || {}
          },
          // Add the captured table data
          currentTableView: tableData,
          // Add the captured filters data
          currentFilters: filtersData,
          // Add recent AI analysis context for agent consultation
          recentAIAnalyses: {
            promptBurst: recentAIAnalyses.promptBurst,
            aiCrawler: recentAIAnalyses.aiCrawler
          },
          // Add property context for property-scoped chat
          property_url: propertyUrl,
          // Add tutorial context if in tutorial mode
          tutorialContentCapture: tutorialContentCapture,
          tutorialMetadata: tutorialMetadata
        })
      });

      if (agentResponse.ok) {
        const responseData = await agentResponse.json();
        
        // Clear sync abort controller since we're switching to polling
        setCurrentAbortController(null);
        
        if (responseData.task_id) {
          
          // Start polling for the async task
          startPolling(responseData.task_id);
          return;
        } else if (responseData.status === 'completed') {
          // Handle successful agent response
          const agentMessage = createAgentMessage(responseData.message, {
            toolsUsed: responseData.tools_used,
            rationale: responseData.rationale,
            action: responseData.action,
            actionData: responseData.data
          }, messages.length);

          // Note: Auto-expand removed for better performance - users can manually expand long messages

          setMessages(prev => [...prev, agentMessage]);
          setIsTyping(false);
          setCurrentAbortController(null); // Clear abort controller on success

          // Trigger sparkle trail for agent actions
          if (responseData.action) {
            detectAgentAction(responseData);
          }

          // Handle all agent actions using shared function
          handleAgentActions(responseData);

          // Call the actual send handler
          onSendMessage(currentMessage, currentContext);
          return;
        }
      } else {
        // Handle error responses from the agent
        const errorData = await agentResponse.json().catch(() => ({}));
        const agentMessage = createErrorMessage(
          `I apologize, but I encountered an issue: ${errorData.message || 'Please try again.'}`,
          {},
          messages.length
        );

        setMessages(prev => [...prev, agentMessage]);
        setIsTyping(false);
        setCurrentAbortController(null); // Clear abort controller on error
        onSendMessage(currentMessage, currentContext);
        return;
      }
    } catch (error) {
      // Clear abort controller
      setCurrentAbortController(null);
      
      // Handle abort errors differently
      if (error.name === 'AbortError') {
        // Request was aborted by user - don't show error message as handleStopThinking already handles it
        return;
      }
      
      // Network or other errors
      console.error('Agent endpoint error:', error);
      const agentMessage = createErrorMessage(
        'I\'m having trouble connecting to the server. Please try again in a moment.',
        {},
        messages.length
      );

      setMessages(prev => [...prev, agentMessage]);
      setIsTyping(false);
      onSendMessage(currentMessage, currentContext);
      return;
    }

    // No fallback - all responses should come from the live agent
  };

  const handleSuggestionClick = (suggestion) => {
    setMessage(suggestion);
    setTimeout(() => handleSendMessage(suggestion), 100);
  };

  // Handle inline button clicks (for property welcome message)
  const handleInlineButtonClick = (action, messageId) => {
    console.log('🔘 [ChatSidebar] Button clicked:', { action, messageId, currentPropertyUrl });
    
    if (action === 'run_initial_assessment') {
      console.log('🎯 [ChatSidebar] Run Initial Assessment clicked - triggering Search Console analysis');
      
      // Disable buttons on the welcome message to prevent multiple clicks
      setMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, buttonsDisabled: true, showInlineButtons: false }
          : msg
      ));
      
      // Set up onboarding context for the tour
      const onboardingContextData = {
        type: 'auto_analysis_started',
        domainContext: {
          url: currentPropertyUrl,
          domain: new URL(currentPropertyUrl).hostname
        },
        matchedProperty: currentPropertyUrl,
        analyzedDomain: new URL(currentPropertyUrl).hostname,
        source: 'property_welcome_button',
        message: `Perfect! I'll analyze ${new URL(currentPropertyUrl).hostname} using your Search Console data. I'm running a comprehensive analysis for the past 30 days to show you exactly what's happening with your SEO performance...`
      };
      
      // Add the analysis running message that the existing useEffect will detect
      const analysisRunningMessage = createAgentMessage(
        `Perfect! I'll analyze ${new URL(currentPropertyUrl).hostname} using your Search Console data. I'm running a comprehensive analysis for the past 30 days to show you exactly what's happening with your SEO performance...`,
        {
          context: { type: 'onboarding_analysis_running', domainContext: onboardingContextData.domainContext }
        },
        messages.length
      );
      setMessages(prev => [...prev, analysisRunningMessage]);
      
      console.log('🎯 [ChatSidebar] Onboarding analysis triggered - message added directly');
      
      // Trigger Search Console analysis using onNavigateToAnalysis
      const analysisData = {
        analysis_type: 'initial_assessment',
        date_ranges: {
          old_period: {
            start_date: getDateDaysAgo(60),
            end_date: getDateDaysAgo(31)
          },
          new_period: {
            start_date: getDateDaysAgo(30),
            end_date: getDateDaysAgo(1)
          }
        },
        country: ''
      };
      
      console.log('🎯 [ChatSidebar] Triggering Search Console analysis:', analysisData);
      onNavigateToAnalysis('trigger_analysis', analysisData);
      
    } else if (action === 'explore_alone') {
      console.log('🎯 [ChatSidebar] Explore Alone clicked - showing acknowledgment');
      
      // Disable buttons on the welcome message
      setMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, buttonsDisabled: true, showInlineButtons: false }
          : msg
      ));
      
      // Just acknowledge the skip
      const skipMessage = createAgentMessage(
        "No problem! Feel free to ask me anything about your SEO performance whenever you're ready. I'm here to help! 🚀",
        {},
        messages.length
      );
      setMessages(prev => [...prev, skipMessage]);
    } else {
      console.log('❓ [ChatSidebar] Unknown button action:', action);
    }
  };

  // Handle stopping current request (both sync and async)
  const handleStopThinking = () => {
    // Stop sync request if active
    if (currentAbortController) {
      currentAbortController.abort();
      setCurrentAbortController(null);
    }
    
    // Stop async polling if active
    stopPolling();
    
    setIsTyping(false);
    
    // Add a friendly message to indicate the request was stopped
    const stopMessage = createSystemMessage(
      '😅 **Oh no... was I taking too long?**\n\nI know sometimes when I\'m analyzing complex data or creating detailed tutorials, it can take me a little longer to get you the perfect advice. \n\n✨ **Feel free to:**\n• Ask me a simpler question\n• Try rephrasing your request\n• Ask me anything else!\n\nI\'m here whenever you\'re ready! 🚀',
      {},
      messages.length
    );
    setMessages(prev => [...prev, stopMessage]);
  };


  // Choice Cards Modal handlers
  const handleChoiceCardSelect = (card) => {

    setIsChoiceCardsModalOpen(false);
    
    // Handle tutorial card selection
    if (card.tutorial_context || card.difficulty || card.estimated_time) {
      
      // Call the tutorial creation API endpoint
      handleTutorialCreation(card);
      return;
    }
    
    // Handle task creation
    if (card.action_payload?.tool_to_call === 'create_task') {

      setActiveTaskCard(card);
      setIsTaskFlowActive(true);

      return;
    }
    
    // Navigate based on card action
    if (card.action_payload?.tool_to_call === 'navigate_to_page') {
      const { page_url, tab } = card.action_payload.args;
      onNavigateToAnalysis('navigate_to_page', { page_url, tab });
    } else if (card.action_payload?.tool_to_call === 'navigate_to_analysis') {
      const { route, filters } = card.action_payload.args;
      // Map route to report_type expected by parent component
      const reportType = ROUTE_MAPPING[route] || 'keywords';
      onNavigateToAnalysis('navigate_to_analysis', { reportType, filters });
    } else if (card.action_payload?.tool_to_call === 'revert_to_table') {
      // Just close the modal - user wants to see the full table

    }
  };

  return (
    <ChatSidebarLayout
      // Layout props
      darkMode={darkMode}
      className={className}
      isTyping={isTyping}
      isCollapsed={isCollapsed}
      
      // Tab state
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      showTabs={showTabs}
      
      // Core chat data
      messages={messages}
      message={message}
      setMessage={setMessage}
      currentDomain={currentDomain}
      messagesEndRef={messagesEndRef}
      // autoExpandingMessage removed for performance
      tourInProgress={tourInProgress}
      setTourInProgress={setTourInProgress}
      currentContext={currentContext}
      
      // Async polling state
      isPolling={isPolling}
      currentProgress={currentProgress}
      currentProgressMessage={currentProgressMessage}
      
      // Modal states
      isChoiceCardsModalOpen={isChoiceCardsModalOpen}
      choiceCardsData={choiceCardsData}
      isExpandedMessageModalOpen={isExpandedMessageModalOpen}
      expandedMessage={expandedMessage}
      isTaskFlowActive={isTaskFlowActive}
      activeTaskCard={activeTaskCard}
      isTutorialModeActive={isTutorialModeActive}
      activeTutorialData={activeTutorialData}
      isAvatarJourneyActive={isAvatarJourneyActive}
      
      // Sparkle trail state
      isTrailActive={isTrailActive}
      sourceElement={sourceElement}
      targetElement={targetElement}
      activeTrail={activeTrail}
      
      // Settings view state
      showSidebarForm={showSidebarForm}
      onShowSidebarForm={() => setShowSidebarForm(true)}
      onHideSidebarForm={() => setShowSidebarForm(false)}
      analysisSettings={analysisSettings}
      
      // Handlers
      onToggleCollapse={onToggleCollapse}
      reopenLastTutorial={reopenLastTutorial}
      onClearConversation={handleClearConversation}
      onStartNewConversation={handleStartNewConversation}
      handleSuggestionClick={handleSuggestionClick}
      handleExpandMessage={handleExpandMessage}
      handleSendMessage={handleSendMessage}
      handleStopThinking={handleStopThinking}
      handleChoiceCardSelect={handleChoiceCardSelect}
      handleChoiceCardsClose={handleChoiceCardsClose}
      handleCloseExpandedMessage={handleCloseExpandedMessage}
      handleTaskGenerated={handleTaskGenerated}
      handleStepComplete={handleStepComplete}
      handleTaskFlowComplete={handleTaskFlowComplete}
      handleTaskClose={handleTaskClose}
      handleAvatarJourneyComplete={handleAvatarJourneyComplete}
      handleTutorialClose={handleTutorialClose}
      completeTrail={completeTrail}
      handleInlineButtonClick={handleInlineButtonClick}
      portalMode={portalMode}
    />
  );
};

export default ChatSidebar;